import SplitHero from '@/components/feature/SplitHero';
import Container from '@/components/feature/Container';
import NumberedSteps from '@/components/feature/NumberedSteps';
import FaqList from '@/components/feature/FaqList';
import Reveal from '@/components/base/Reveal';
import Eyebrow from '@/components/base/Eyebrow';
import Photo from '@/components/base/Photo';
import PillLink from '@/components/base/PillLink';
import { careOptions, careFaqs } from '@/mocks/care';

const principles = [
  { t: 'Personal understanding', d: 'We start by getting to know the person \u2014 their history, routines, likes and the people who matter to them.' },
  { t: 'Everyday dignity', d: 'Support is offered with respect and discretion, always protecting choice and independence.' },
  { t: 'Meaningful connection', d: 'Friendship, family and shared moments are as important to a good day as any care plan.' },
];

const [a, b, c, d] = careOptions;

export default function OurCare() {
  return (
    <>
      <SplitHero
        eyebrow="Our Care"
        title={<>Care built around <span className="em-serif">the person.</span></>}
        intro="Thoughtful support that respects dignity, honours personal preferences and adapts as life changes, always shaped with each person, never simply for them."
        img="https://public.readdy.ai/ai/img_res/sandbox/d9f57693db1a3f647bcb80a40a4edde0/5fa7b452-069c-4ae4-8f43-36e8851e8c88_dw-oc-hero.webp"
        alt="Older woman talking with a care team member over tea"
      />

      <section className="py-16 md:py-28 bg-background-100">
        <Container className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-4">
            <Eyebrow>Our Approach</Eyebrow>
            <h2 className="mt-5 text-[32px] md:text-[46px] leading-[1.08] font-medium tracking-[-0.02em]">Listening comes <span className="em-serif">first.</span></h2>
          </Reveal>
          <div className="lg:col-span-8">
            {principles.map((p, i) => (
              <Reveal key={p.t} delay={i * 80}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 py-7 border-t border-background-200">
                  <h3 className="md:col-span-5 text-[22px] md:text-[26px] font-medium">{p.t}</h3>
                  <p className="md:col-span-7 text-[16px] leading-relaxed text-foreground-700">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-28">
        <Container>
          <Reveal>
            <Eyebrow>Care Options</Eyebrow>
            <h2 className="mt-5 text-[32px] md:text-[46px] leading-[1.08] font-medium tracking-[-0.02em] max-w-xl">Ways we can <span className="em-serif">help.</span></h2>
          </Reveal>
          <div className="mt-14 flex flex-col gap-20 md:gap-32">
            <div className="flex flex-col gap-14 md:gap-8 lg:gap-12">
            <Reveal>
              <article className="relative w-full h-[420px] sm:h-[460px] lg:h-[540px] rounded-2xl md:rounded-[20px] overflow-hidden">
                <Photo className="absolute inset-0 w-full h-full" reveal="right" revealVariant="wide" src={a.img} alt={a.alt} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent pointer-events-none"></div>
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-10 lg:p-12 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-14 items-end text-white">
                  <div className="lg:col-span-6">
                    <p className="text-[13px] text-white/80">01</p>
                    <h3 className="mt-3 text-[28px] md:text-[40px] leading-[1.08] font-medium tracking-[-0.02em]">{a.title}</h3>
                  </div>
                  <p className="lg:col-span-5 lg:col-start-8 text-[15px] md:text-[16px] leading-relaxed text-white/90">{a.desc}</p>
                </div>
              </article>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-8 lg:gap-12 items-stretch">
              {[b, c].map((o, i) => (
                <Reveal key={o.title} delay={i * 100} className="h-full">
                  <article className={`h-full flex gap-7 ${i === 1 ? 'flex-col md:flex-col-reverse md:justify-between' : 'flex-col'}`}>
                    <Photo className="w-full h-[320px] md:h-[440px] lg:h-[560px] rounded-2xl shrink-0" reveal="down" revealGroup="care-options" revealVariant="portrait" src={o.img} alt={o.alt} />
                    <div className={i === 1 ? 'flex flex-col' : 'md:flex-1 flex flex-col'}>
                      <p className="text-[13px] text-foreground-600">0{i + 2}</p>
                      <h3 className="mt-2 text-[24px] md:text-[30px] leading-tight font-medium tracking-[-0.01em]">{o.title}</h3>
                      <p className="mt-4 text-[16px] leading-relaxed text-foreground-700">{o.desc}</p>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>

            <Reveal>
              <article className="relative w-full h-[420px] sm:h-[460px] lg:h-[540px] rounded-2xl md:rounded-[20px] overflow-hidden">
                <Photo className="absolute inset-0 w-full h-full" reveal="right" revealVariant="wide" src={d.img} alt={d.alt} />
                <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/20 to-transparent pointer-events-none"></div>
                <div className="absolute inset-x-0 top-0 p-6 md:p-10 lg:p-12 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-14 items-start text-white">
                  <div className="lg:col-span-6">
                    <p className="text-[13px] text-white/80">04</p>
                    <h3 className="mt-3 text-[28px] md:text-[40px] leading-[1.08] font-medium tracking-[-0.02em]">{d.title}</h3>
                  </div>
                  <p className="lg:col-span-5 lg:col-start-8 lg:pt-8 text-[15px] md:text-[16px] leading-relaxed text-white/90">{d.desc}</p>
                </div>
              </article>
            </Reveal>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-28 bg-accent-100/60">
        <Container>
          <Reveal>
            <Eyebrow>How It Works</Eyebrow>
            <h2 className="mt-5 mb-12 md:mb-16 text-[32px] md:text-[46px] leading-[1.08] font-medium tracking-[-0.02em]">Three simple <span className="em-serif">steps.</span></h2>
          </Reveal>
          <NumberedSteps
            steps={[
              { t: 'Let\u2019s Talk', d: 'Tell us about your loved one and what you\u2019re looking for. We\u2019ll listen and answer honestly.' },
              { t: 'Visit & Explore', d: 'Come and see the home, meet the team and get a feel for everyday life.' },
              { t: 'Plan Together', d: 'We shape a plan with you and your family, then settle in at a comfortable pace.' },
            ]}
          />
        </Container>
      </section>

      <section className="py-16 md:py-28">
        <Container className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-4">
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="mt-5 text-[32px] md:text-[42px] leading-[1.08] font-medium tracking-[-0.02em]">Questions families <span className="em-serif">often ask.</span></h2>
          </Reveal>
          <div className="lg:col-span-8"><FaqList items={careFaqs} idPrefix="care-faq" /></div>
        </Container>
      </section>

      <section>
        <div>
          <div>
            <div className="relative h-[520px] md:h-[680px] overflow-hidden" data-dw-reveal="right" data-dw-reveal-variant="wide" data-dw-reveal-layer="under">
              <img
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover object-center"
                alt="Older couple holding hands on a garden bench at sunset"
                title="Finding the right next step with Daywell Living"
                src="https://public.readdy.ai/ai/img_res/sandbox/d9f57693db1a3f647bcb80a40a4edde0/5fa7b452-069c-4ae4-8f43-36e8851e8c88_dw-oc-cta.webp"
              />
              <div className="absolute inset-0 bg-black/40" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 text-background-50">
                <h2 className="text-[34px] md:text-[56px] leading-[1.05] font-medium tracking-[-0.02em] max-w-2xl">Let&rsquo;s find the right <span className="em-serif">next step.</span></h2>
                <div className="mt-8"><PillLink to="/visit#enquiry" variant="light">Book a Visit</PillLink></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}